import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Users, ClipboardList, Receipt } from 'lucide-react';
import { Input } from './ui/input';
import { searchApi } from '../api';

const GROUPS = [
  { key: 'clients', label: 'Clients', icon: Users, title: (r) => r.name, sub: (r) => r.entityType || r.contact || '', link: (r) => `/dashboard/clients?open=${r.id}` },
  { key: 'tasks', label: 'Tasks', icon: ClipboardList, title: (r) => r.title, sub: (r) => [r.clientName, r.status, r.dueDate && new Date(r.dueDate).toLocaleDateString()].filter(Boolean).join(' · '), link: (r) => `/dashboard/tasks?highlight=${r.id}` },
  { key: 'invoices', label: 'Invoices', icon: Receipt, title: (r) => r.number, sub: (r) => `${r.clientName || ''} · R ${(r.total || 0).toLocaleString()} · ${r.status}`, link: (r) => `/dashboard/billing?open=${r.id}` },
];

export const GlobalSearch = () => {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      searchApi.query(q.trim()).then(setRes).catch(() => setRes(null)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (link) => { setOpen(false); setQ(''); nav(link); };
  const total = res ? GROUPS.reduce((n, g) => n + (res[g.key]?.length || 0), 0) : 0;

  return (
    <div ref={wrap} className="flex-1 max-w-xl relative hidden md:block">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        placeholder="Search clients, tasks, invoices..."
        className="pl-9 h-10 bg-stone-50 border-stone-200"
        data-testid="global-search-input"
      />
      {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-stone-400"/>}
      {open && q.trim().length >= 2 && res && (
        <div className="absolute left-0 right-0 mt-2 rounded-xl border border-stone-200 bg-white shadow-xl max-h-[70vh] overflow-y-auto z-50" data-testid="global-search-results">
          {total === 0 && <div className="p-5 text-sm text-stone-500 text-center" data-testid="global-search-empty">No matches for &ldquo;{q}&rdquo;</div>}
          {GROUPS.filter((g) => res[g.key]?.length).map((g) => (
            <div key={g.key} className="py-1.5 border-b border-stone-100 last:border-0" data-testid={`global-search-group-${g.key}`}>
              <div className="px-4 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-semibold text-stone-500">{g.label}</div>
              {res[g.key].map((r) => (
                <button key={r.id} type="button" onClick={() => go(g.link(r))} className="w-full text-left px-4 py-2 flex items-center gap-3 hover:bg-emerald-50/60 transition-colors" data-testid={`global-search-result-${g.key}-${r.id}`}>
                  <g.icon size={15} className="text-emerald-800 flex-shrink-0"/>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{g.title(r)}</div>
                    <div className="text-xs text-stone-500 truncate">{g.sub(r)}</div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
